import { type LedgerEntry, type RequestLedger, RequestOriginGroup, RequestOriginKind } from "./models";

/**
 * The Origin filter's bucket for each kind of opening. `null` for what isn't a cause anyone can filter by: removals,
 * and the "not recorded" / "unknown" entries, which have no recorded cause. A merged-in entry has no bucket of its own:
 * its causes are the merged request's own entries (`originGroupsOf`).
 */
export const ORIGIN_GROUP_BY_KIND: Readonly<Record<RequestOriginKind, RequestOriginGroup | null>> = {
	[RequestOriginKind.CallOff]: RequestOriginGroup.People,
	[RequestOriginKind.TimeOff]: RequestOriginGroup.People,
	[RequestOriginKind.Deactivation]: RequestOriginGroup.People,
	[RequestOriginKind.PostedByPerson]: RequestOriginGroup.People,
	[RequestOriginKind.FromRecommendation]: RequestOriginGroup.People,
	// Someone asked Annie to post it.
	[RequestOriginKind.PostedByAnnie]: RequestOriginGroup.People,
	[RequestOriginKind.ReplaceRequest]: RequestOriginGroup.People,
	[RequestOriginKind.SwapRequest]: RequestOriginGroup.People,
	[RequestOriginKind.CountRaised]: RequestOriginGroup.People,
	// The rest of a merge an admin made.
	[RequestOriginKind.CountAdjusted]: RequestOriginGroup.People,
	[RequestOriginKind.AutoPost]: RequestOriginGroup.Automation,
	// InHouse opens it so staff can replace an agency worker before the shift.
	[RequestOriginKind.ReplacingAgency]: RequestOriginGroup.Automation,
	[RequestOriginKind.Agency]: RequestOriginGroup.External,
	// The agency unscheduled its own worker.
	[RequestOriginKind.AgencyCanceled]: RequestOriginGroup.External,
	[RequestOriginKind.MergedIn]: null,
	[RequestOriginKind.CountLowered]: null,
	[RequestOriginKind.Filled]: null,
	[RequestOriginKind.Canceled]: null,
	[RequestOriginKind.MergedAway]: null,
	[RequestOriginKind.AgencySyncLowered]: null,
	[RequestOriginKind.NotRecorded]: null,
	[RequestOriginKind.Unknown]: null,
};

/** The filter's display order. */
export const ORIGIN_GROUPS: readonly RequestOriginGroup[] = [RequestOriginGroup.Automation, RequestOriginGroup.People, RequestOriginGroup.External];

/** Entries that opened slots. Removals and zero-delta entries are not causes. */
export const openingsOf = (entries: readonly LedgerEntry[]): LedgerEntry[] => entries.filter((entry) => entry.delta > 0);

const groupsOfEntries = (entries: readonly LedgerEntry[]): RequestOriginGroup[] =>
	openingsOf(entries).flatMap((entry) => {
		if (entry.details.kind === RequestOriginKind.MergedIn) return groupsOfEntries(entry.details.entries);
		const group = ORIGIN_GROUP_BY_KIND[entry.kind];
		return group === null ? [] : [group];
	});

/** Every bucket a request's openings fall in, a merged-in request's own openings included. */
export const originGroupsOf = (ledger: RequestLedger): ReadonlySet<RequestOriginGroup> => new Set(groupsOfEntries(ledger.entries));

/** No groups chosen means no filter. A request matches when ANY of its openings is in a chosen group. */
export const matchesOriginGroups = (ledger: RequestLedger | undefined, groups: readonly RequestOriginGroup[]): boolean => {
	if (groups.length === 0) return true;
	if (ledger === undefined) return false;
	const own = originGroupsOf(ledger);
	return groups.some((group) => own.has(group));
};

/** How many of `requestIds` have at least one opening in each group — a request with openings in two groups counts in both. */
export const countByOriginGroup = (requestIds: readonly string[], byRequestId: ReadonlyMap<string, RequestLedger>): Record<RequestOriginGroup, number> => {
	const counts: Record<RequestOriginGroup, number> = {
		[RequestOriginGroup.Automation]: 0,
		[RequestOriginGroup.People]: 0,
		[RequestOriginGroup.External]: 0,
	};
	for (const id of requestIds) {
		const ledger = byRequestId.get(id);
		if (ledger === undefined) continue;
		for (const group of originGroupsOf(ledger)) counts[group] += 1;
	}
	return counts;
};
